/**
 * Site / company access switches for User Details modal (Related Companies tab).
 */

import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Building2, Power, FileCheck, CheckCircle, ShieldCheck, UserCheck, Eye, Bell,
  CircleDollarSign,
} from 'lucide-react';
import type { UserProfile, UserSitePermission } from '../../lib/api';

function Label({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={className}>{children}</div>;
}

export interface UserDetailsSiteAccessTabProps {
  userSitePermissions: UserSitePermission[];
  savingPermission: number | null;
  selectedUser: UserProfile;
  isSelf: (userProfile: UserProfile) => boolean;
  handleToggleSiteLink: (siteId: number, isLinked: boolean) => Promise<void>;
  handleToggleSitePermission: (siteId: number, permission: string, value: boolean) => Promise<void>;
}

export function UserDetailsSiteAccessTab({
  userSitePermissions,
  savingPermission,
  selectedUser,
  isSelf,
  handleToggleSiteLink,
  handleToggleSitePermission,
}: UserDetailsSiteAccessTabProps) {
  if (userSitePermissions.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Loading company access...</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {userSitePermissions.map((perms) => {
        // CR-5311 round 2: backend now prepends a synthetic group entry
        // (is_group_entry=true, site=null) so the Related Companies tab
        // shows the user's group membership alongside the sibling sites.
        // The entry is read-only — per-site module toggles stay on the
        // individual site rows below.
        const isGroup = (perms as { is_group_entry?: boolean }).is_group_entry === true;
        const cardKey = isGroup ? `group-${(perms as { group_id?: number }).group_id ?? 'x'}` : perms.site;
        const isSaving = !isGroup && savingPermission === perms.site;
        return (
          <Card
            key={cardKey}
            className={`${perms.is_linked ? '' : 'opacity-60'} ${isSaving ? 'animate-pulse' : ''} ${isGroup ? 'border-primary/40 bg-primary/5' : ''}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {perms.site_name}
                  {isGroup && (
                    <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/15 text-primary uppercase">
                      Group
                    </span>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {perms.is_linked ? 'Linked' : 'Not Linked'}
                  </span>
                  <Switch
                    checked={perms.is_linked}
                    onCheckedChange={(checked) => handleToggleSiteLink(perms.site, checked)}
                    disabled={isSelf(selectedUser) || isSaving || isGroup}
                  />
                </div>
              </div>
              {isGroup ? (
                <p className="text-xs text-muted-foreground">
                  {(perms as { role_name?: string }).role_name
                    ? `Group membership · Role: ${(perms as { role_name?: string }).role_name}`
                    : 'Group membership — manage per-site access via the cards below.'}
                </p>
              ) : perms.site_code ? (
                <p className="text-xs text-muted-foreground">{perms.site_code}</p>
              ) : null}
            </CardHeader>

            {perms.is_linked ? (
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Power className="h-4 w-4" />
                    Active
                  </span>
                  <Switch
                    checked={perms.is_active}
                    onCheckedChange={(checked) =>
                      handleToggleSitePermission(perms.site, 'is_active', checked)
                    }
                    disabled={isSelf(selectedUser) || isSaving}
                  />
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Access Modules
                  </Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm flex items-center gap-2">
                        <FileCheck className="h-4 w-4" />
                        Purchasing
                      </span>
                      <Switch
                        checked={perms.can_purchasing}
                        onCheckedChange={(checked) =>
                          handleToggleSitePermission(perms.site, 'can_purchasing', checked)
                        }
                        disabled={isSelf(selectedUser) || perms.is_read_only || isSaving}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Quality
                      </span>
                      <Switch
                        checked={perms.can_quality}
                        onCheckedChange={(checked) =>
                          handleToggleSitePermission(perms.site, 'can_quality', checked)
                        }
                        disabled={isSelf(selectedUser) || perms.is_read_only || isSaving}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm flex items-center gap-2">
                        <CircleDollarSign className="h-4 w-4" />
                        Finance
                      </span>
                      <Switch
                        checked={perms.can_finance}
                        onCheckedChange={(checked) =>
                          handleToggleSitePermission(perms.site, 'can_finance', checked)
                        }
                        disabled={isSelf(selectedUser) || perms.is_read_only || isSaving}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    User Role
                  </Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4" />
                        Administrator
                      </span>
                      <Switch
                        checked={perms.is_admin}
                        onCheckedChange={(checked) =>
                          handleToggleSitePermission(perms.site, 'is_admin', checked)
                        }
                        disabled={isSelf(selectedUser) || perms.is_read_only || isSaving}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm flex items-center gap-2">
                        <UserCheck className="h-4 w-4" />
                        Approval
                      </span>
                      <Switch
                        checked={perms.can_approve}
                        onCheckedChange={(checked) =>
                          handleToggleSitePermission(perms.site, 'can_approve', checked)
                        }
                        disabled={isSelf(selectedUser) || perms.is_read_only || isSaving}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        Read Only
                      </span>
                      <Switch
                        checked={perms.is_read_only}
                        onCheckedChange={(checked) =>
                          handleToggleSitePermission(perms.site, 'is_read_only', checked)
                        }
                        disabled={
                          isSelf(selectedUser) ||
                          perms.is_admin ||
                          perms.can_approve ||
                          isSaving
                        }
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Notification Emails
                  </Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm flex items-center gap-2">
                        <Bell className="h-4 w-4" />
                        Procurement
                      </span>
                      <Switch
                        checked={perms.notify_procurement}
                        onCheckedChange={(checked) =>
                          handleToggleSitePermission(perms.site, 'notify_procurement', checked)
                        }
                        disabled={isSaving}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm flex items-center gap-2">
                        <Bell className="h-4 w-4" />
                        Quality
                      </span>
                      <Switch
                        checked={perms.notify_quality}
                        onCheckedChange={(checked) =>
                          handleToggleSitePermission(perms.site, 'notify_quality', checked)
                        }
                        disabled={isSaving}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            ) : null}
          </Card>
        );
      })}
    </div>
  );
}
